import React, { useState } from "react";
import { Button, Container, Form, Table } from "react-bootstrap";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { formatDate, formatDateFromAPI } from "../helpers/functions";
import MyGraph from "./MyGraph";
import { selectUser } from "../store/userSlice";
import { useSelector } from "react-redux";

const commsOptions = [
  { value: "sms", label: "Sms" },
  { value: "email", label: "Email" },
  { value: "voice", label: "Voice" },
  { value: "threaded", label: "Threaded" },
  { value: "inbound", label: "Inbound" },
];
const levelOptions = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "danger", label: "Danger" },
];

export default function Alerts() {
  let [searchParams, setSearchParams] = useSearchParams();

  // get user
  const user = useSelector(selectUser);

  // pagination in search params
  const limit = parseInt(searchParams.get("limit") || 10);
  const offset = parseInt(searchParams.get("offset") || 0);
  // order
  const order = searchParams.get("order") || "desc";
  const orderBy = searchParams.get("orderBy") || "date_at";
  // filters in search params
  const dbRef = searchParams.get("dbRef") || "";
  const countries = searchParams.getAll("countries");
  const accounts = searchParams.getAll("accounts");
  const comms = searchParams.getAll("comms");
  const rules = searchParams.getAll("rules");
  const level = searchParams.getAll("level");

  const today = new Date(new Date(new Date().setHours(0)).setMinutes(0));
  const startDate = new Date(searchParams.get("startDate") || today);
  const endDate = new Date(
    searchParams.get("endDate") || new Date(today.setDate(today.getDate() + 1))
  );

  const [dbInfo, setDbInfo] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [dbRefOptions, setDbRefOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [countriesOptions, setCountriesOptions] = useState([]);
  const [rulesOptions, setRulesOptions] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  const getRecords = async () => {
    let headersList = {
      Accept: "*/*",
    };

    let response = await fetch(
      `/auth/alerts?db_ref=${dbRef || ""}${accounts
        .map((a) => `&account_id=${a}`)
        .join("")}${comms.map((c) => `&kind=${c}`).join("")}${level
        .map((l) => `&level=${l}`)
        .join("")}${rules.map((r) => `&rule=${r}`).join("")}${countries
        .map((c) => `&mcc=${c}`)
        .join("")}&start_date=${formatDate(startDate)}&end_date=${formatDate(
        endDate
      )}&order=${order}&order_by=${orderBy}&limit=${limit}&offset=${offset}`,
      {
        method: "GET",
        headers: headersList,
      }
    );

    let data = await response.json();
    if (response.ok) {
      setRecords(data.data || []);
      setTotal(data.total);
    } else {
      toast.error(data.error);
      if (response.status === 401) {
        if (user) {
          toast.error("Your session has expired. You need to login again.");
        }
        navigate("/login", { state: { from: location } });
      }
    }
  };

  const getDbInfo = async () => {
    let headersList = {
      Accept: "*/*",
    };

    let response = await fetch("/auth/db-info", {
      method: "GET",
      headers: headersList,
    });

    let data = await response.json();
    if (response.ok) {
      setDbRefOptions(
        data.map((d) => ({
          value: d.DbRef,
          label: d.DbRef,
          data: d,
        }))
      );
      if (data.length >= 1) {
        let db;
        if (dbRef) {
          db = data.find((d) => d.DbRef === dbRef);
        } else {
          db = data[0];
          searchParams.set("dbRef", db?.DbRef);
          setSearchParams(searchParams, { replace: true });
        }
        if (!db) {
          toast.error("Database not found");
          return;
        }
        setDbInfo(db);
        setAccountOptions(
          db?.Accounts?.map((a) => ({ value: a.Id, label: a.Label })) ?? []
        );
        setCountriesOptions(
          db?.Countries.map((c) => ({
            value: c.Id,
            label: c.Label,
          }))
        );
      }
    } else {
      toast.error(data.error);
      if (response.status === 401) {
        if (user) {
          toast.error("Your session has expired. You need to login again.");
        }
        navigate("/login", { state: { from: location } });
      }
    }
  };

  const getRulesOptions = async () => {
    let headersList = {
      Accept: "*/*",
    };

    let response = await fetch(`/auth/rules`, {
      method: "GET",
      headers: headersList,
    });

    let data = await response.json();
    if (response.ok) {
      setRulesOptions(
        data.data.map((d) => ({ value: d.Id, label: d.Description }))
      );
    } else {
      toast.error(data.error);
      if (response.status === 401) {
        if (user) {
          toast.error("Your session has expired. You need to login again.");
        }
        navigate("/login", { state: { from: location } });
      }
    }
  };

  useEffect(() => {
    if (dbRef) {
      getRecords();
    }
    if (!dbInfo || !dbRef) {
      getDbInfo();
    }
  }, [location]);

  useEffect(() => {
    getRulesOptions();
  }, []);

  useEffect(() => {
    if (dbRef) {
      const data = dbRefOptions.find((o) => o.value === dbRef);
      if (data) {
        setDbInfo(data.data);
        setAccountOptions(
          data?.data?.Accounts?.map((a) => ({
            value: a.Id,
            label: a.Label,
          })) ?? []
        );

        setCountriesOptions(
          data?.data?.Countries.map((c) => ({
            value: c.Id,
            label: c.Label,
          })) ?? []
        );
      }
    }
  }, [dbRef]);

  return (
    <Container fluid>
      <div>
        <MyGraph page="alerts" title="Alerts" />
      </div>

      <div>
        <div className="my-2 row g-3 align-items-end">
          <div className="col-12 col-md-6 col-xl-3">
            <div>Db ref</div>
            <Select
              value={dbRefOptions.find((o) => o.value === dbRef)}
              onChange={(o) => {
                if (o) {
                  searchParams.set("dbRef", o?.value ?? "");
                  setSearchParams(searchParams);
                } else {
                  searchParams.delete("dbRef");
                  searchParams.delete("countries");
                  searchParams.delete("accounts");
                  setSearchParams(searchParams);
                  setDbInfo(null);
                  setAccountOptions([]);
                  setCountriesOptions([]);
                }
              }}
              options={dbRefOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Comms</div>
            <Select
              value={commsOptions.filter((c) => comms.includes(c.value))}
              onChange={(values) => {
                searchParams.delete("comms");
                values.forEach((v) => searchParams.append("comms", v.value));
                setSearchParams(searchParams);
              }}
              isMulti
              options={commsOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Countries</div>
            <Select
              value={countriesOptions.filter((o) =>
                countries.includes(o.value.toString())
              )}
              onChange={(values) => {
                searchParams.delete("countries");
                values.forEach((v) =>
                  searchParams.append("countries", v.value)
                );
                setSearchParams(searchParams);
              }}
              isMulti
              options={countriesOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Account</div>
            <Select
              value={accountOptions.filter((o) =>
                accounts.includes(o.value.toString())
              )}
              onChange={(values) => {
                searchParams.delete("accounts");
                values.forEach((v) => searchParams.append("accounts", v.value));
                setSearchParams(searchParams);
              }}
              isMulti
              options={accountOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Rules</div>
            <Select
              value={rulesOptions.filter((r) =>
                rules.includes(r.value.toString())
              )}
              onChange={(values) => {
                searchParams.delete("rules");
                values.forEach((v) => searchParams.append("rules", v.value));
                setSearchParams(searchParams);
              }}
              isMulti
              options={rulesOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Alert Level</div>
            <Select
              value={levelOptions.filter((l) => level.includes(l.value))}
              onChange={(values) => {
                searchParams.delete("level");
                values.forEach((v) => searchParams.append("level", v.value));
                setSearchParams(searchParams);
              }}
              isMulti
              options={levelOptions}
              maxMenuHeight={150}
            />{" "}
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Start Date</div>
            <DatePicker
              showTimeSelect
              selected={startDate}
              onChange={(date) => {
                searchParams.set("startDate", date.toISOString());
                setSearchParams(searchParams);
              }}
              className="form-control"
              dateFormat={"dd-MM-yyyy HH:mm"}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>End Date</div>
            <DatePicker
              showTimeSelect
              selected={endDate}
              onChange={(date) => {
                searchParams.set("endDate", date.toISOString());
                setSearchParams(searchParams);
              }}
              className="form-control"
              dateFormat={"dd-MM-yyyy HH:mm"}
            />
          </div>

          {/* <div className="col-6 col-md-3 ms-auto text-end"></div> */}

          <div className="col-12 col-md-6 col-xl-3 ms-auto text-end">
            <Button
              className="btn-secondary btn-sm me-3 "
              onClick={() => {
                getRecords();
              }}
            >
              Refresh
            </Button>
            <Button
              className="btn-secondary btn-sm"
              onClick={() => {
                setSearchParams(new URLSearchParams({}));
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        <Table striped bordered hover className="mt-4">
          <thead>
            <tr>
              <th>ID</th>
              <th>Rule</th>
              <th>Db Ref</th>
              <th>Alert Level</th>
              <th>
                <button
                  className="btn p-0 fw-bold"
                  onClick={() => {
                    if (orderBy === "date_at") {
                      if (order === "asc") {
                        searchParams.set("order", "desc");
                      } else {
                        searchParams.set("order", "asc");
                      }
                    } else {
                      searchParams.set("order", "desc");
                      searchParams.set("orderBy", "date_at");
                    }
                    setSearchParams(searchParams);
                  }}
                >
                  Date{" "}
                  {orderBy === "date_at" ? (
                    order === "desc" ? (
                      <FontAwesomeIcon icon={faChevronDown} />
                    ) : (
                      <FontAwesomeIcon icon={faChevronUp} />
                    )
                  ) : (
                    ""
                  )}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {records.map((record) => (
              <tr key={record.Id}>
                <td>
                  <Link
                    to={{
                      pathname: `/alerts/${record.Id}`,
                      search: location.search,
                    }}
                  >
                    #{String(record.Id).padStart(6, "0")}
                  </Link>
                </td>
                <td>
                  <Link to={`/rules/${record.Rule.Id}`}>
                    {record.Rule.Description}
                  </Link>
                </td>
                <td>
                  <Button
                    variant="link"
                    onClick={() => {
                      searchParams.set("dbRef", record.DbRef ?? "");
                      setSearchParams(searchParams);
                    }}
                  >
                    {record.DbRef}
                  </Button>
                </td>
                <td>
                  <span className={`badge bg-${record.Level}`}>
                    {record.Level}
                  </span>
                </td>
                <td>{formatDateFromAPI(record.DateAt)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="d-flex justify-content-between  align-items-center mt-5 gap-3 ">
          <div>
            <span>{total} alerts found</span>
          </div>
          <div>
            <Button
              disabled={offset === 0}
              className="btn-sm me-4"
              variant="secondary"
              onClick={() => {
                searchParams.set("offset", offset - limit);
                setSearchParams(searchParams);
              }}
            >
              Previous
            </Button>
            <span className="fw-bold"> Page {offset / limit + 1} </span>
            <Button
              disabled={offset + limit >= total}
              variant="secondary"
              className="btn-sm ms-4"
              onClick={() => {
                searchParams.set("offset", offset + limit);
                setSearchParams(searchParams);
              }}
            >
              Next
            </Button>
          </div>
          <div>
            Show
            <select
              className="btn"
              value={limit}
              onChange={(e) => {
                searchParams.set("offset", 0);
                searchParams.set("limit", e.target.value);
                setSearchParams(searchParams);
              }}
            >
              <option value={2}>2</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            alerts per page.
          </div>
        </div>
      </div>
      <Outlet />
    </Container>
  );
}
